import { motion } from "framer-motion";
import { BRAND } from "@/lib/api";

/**
 * Sticker — renders a sub-region of the master cartoon illustration as a
 * floating, animated decorative sticker. Coordinates are tuned visually
 * against the uploaded illustration grid.
 */
export const Sticker = ({
    pos = "0% 0%",
    size = "1000% auto",
    width = 96,
    height = 96,
    rotate = 0,
    className = "",
    delay = 0,
    float = "y",
    style = {},
}) => {
    const anim = float === "big" ? "float-y-big" : float === "slow" ? "float-y-slow" : "float-y";
    return (
        <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay, ease: "easeOut" }}
            className={`pointer-events-none absolute ${anim} ${className}`}
            style={{ width, height, ...style }}
        >
            <div
                className="sticker h-full w-full"
                style={{
                    "--sticker-src": `url("${BRAND.illustration}")`,
                    "--sticker-pos": pos,
                    "--sticker-size": size,
                    "--rot": `${rotate}deg`,
                    transform: `rotate(${rotate}deg)`,
                }}
            />
        </motion.div>
    );
};

/**
 * IconSticker — colorful badge with an icon + soft shadow.
 * Used as a decorative cartoon-style accent.
 */
export const IconSticker = ({
    icon: Icon,
    tone = "orange",
    size = 56,
    className = "",
    rotate = 0,
    delay = 0,
}) => {
    const tones = {
        orange: "bg-[#FDF1E7] text-[#F26B1F]",
        navy: "bg-[#0E2A47] text-white",
        cream: "bg-[#FAF6EE] text-[#0E2A47]",
        white: "bg-white text-[#0E2A47]",
        emerald: "bg-emerald-50 text-emerald-700",
    };
    return (
        <motion.span
            initial={{ opacity: 0, scale: 0.7, y: 10 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-20px" }}
            transition={{ duration: 0.5, delay, ease: "backOut" }}
            className={`inline-flex items-center justify-center rounded-2xl shadow-[0_12px_24px_rgba(14,42,71,0.12)] ring-1 ring-black/5 ${tones[tone] || tones.orange} ${className}`}
            style={{
                width: size,
                height: size,
                transform: `rotate(${rotate}deg)`,
            }}
        >
            <Icon size={Math.round(size * 0.45)} strokeWidth={1.8} />
        </motion.span>
    );
};

export default Sticker;
